import {API_BASE} from './reports';
import buildHeaders from './fetchHeaders';
import {stripReportSaysPrefix} from '../stripReportSaysPrefix';
import {getDeltaOperationRunId} from './fintelDeltaRunContext';

function headersWithOptionalDeltaRun({reportId, deltaMode, base}) {
    const h = buildHeaders(base);
    if (reportId != null && String(reportId).trim() && deltaMode != null && String(deltaMode).trim()) {
        h['X-AWP-Operation-Run-Id'] = getDeltaOperationRunId({reportId, deltaMode});
    }
    return h;
}

function buildEmptyMentionsResponse() {
    return {
        meta: {
            totalEntities: 0,
            totalMentions: 0,
        },
        entities: [],
    };
}

function buildEmptyClustersResponse(entityId = '') {
    return {
        entityId,
        entityLabel: '',
        entityDescription: '',
        publishedAfter: '',
        clusteredAt: '',
        fromCache: false,
        clusters: [],
    };
}

function buildEmptyDeltaClusterList() {
    return {
        publishedAfter: null,
        clusteredAt: null,
        fromCache: false,
        clusters: [],
        claimGroups: [],
    };
}

function normalizeSegmentForUi(seg) {
    if (!seg || typeof seg !== 'object') return null;
    return {
        ...seg,
        segmentIndex: seg.segmentIndex ?? seg.index ?? 0,
        offset: seg.segmentOffset ?? seg.offset ?? 0,
    };
}

/**
 * Map {@code EntityDeltaClusterResponse.DeltaClusterInfo} to the story card shape used by DeltaStoriesView.
 */
function normalizeDeltaClusterForUi(cluster) {
    if (!cluster || typeof cluster !== 'object') return null;
    const rawSeg = Array.isArray(cluster.segments) ? cluster.segments : [];
    const segments = rawSeg.map(normalizeSegmentForUi).filter(Boolean);
    return {
        clusterId: cluster.clusterId,
        title: cluster.name ?? '',
        description: cluster.change ?? '',
        tags: [],
        segments,
        significance: cluster.significance,
        targetEntityLabels: Array.isArray(cluster.targetEntityLabels) ? cluster.targetEntityLabels : [],
        targetEntityIds: Array.isArray(cluster.targetEntityIds) ? cluster.targetEntityIds : [],
        claimUpdates: cluster.claimUpdates,
    };
}

/**
 * LLM sometimes emits a "finding" that only states absence of support in newer segments; those are not corpus updates.
 * @param {string} combined summary + sourceUpdate (lowercased by caller)
 */
export function isAbsenceOnlyCorpusNarrative(combined) {
    if (!combined || typeof combined !== 'string') return false;
    const t = combined.trim().toLowerCase();
    if (t.length < 14) return false;
    if (t.startsWith('no newer')) return true;
    return (
        t.includes('no newer segment') ||
        t.includes('no newer sources') ||
        t.includes('do not identify') ||
        t.includes('does not identify') ||
        t.includes("doesn't identify") ||
        t.includes('no segment claims') ||
        t.includes('no qualifying update') ||
        t.includes('cannot ground') ||
        t.includes('fail to show') ||
        t.includes('does not corroborate') ||
        t.includes('do not corroborate')
    );
}

function normalizeClaimUpdateForUi(u) {
    if (!u || typeof u !== 'object') return null;
    const rawSeg = Array.isArray(u.segments) ? u.segments : [];
    const segments = rawSeg.map(normalizeSegmentForUi).filter(Boolean);
    const id = u.updateId != null ? String(u.updateId) : null;
    if (!id) return null;
    const summary = u.summary ?? '';
    const sourceUpdate = u.sourceUpdate ?? '';
    if (isAbsenceOnlyCorpusNarrative(`${summary} ${sourceUpdate}`)) {
        return null;
    }
    return {
        updateId: id,
        name: u.name ?? '',
        summary,
        sourceUpdate,
        relation: u.relation ?? null,
        significance: u.significance,
        segments,
    };
}

/**
 * @param {object} g raw claim group from API
 * @param {number} index
 */
function normalizeClaimGroupForUi(g, index) {
    if (!g || typeof g !== 'object') return null;
    const updates = (Array.isArray(g.updates) ? g.updates : [])
        .map((u) => normalizeClaimUpdateForUi(u))
        .filter(Boolean);
    if (updates.length === 0) return null;
    const claimKey =
        g.claimId != null && String(g.claimId).trim()
            ? String(g.claimId).trim()
            : `claim-${index}`;
    const sigs = updates
        .map((u) => u.significance)
        .filter((s) => s != null && Number.isFinite(Number(s)))
        .map(Number);
    const maxFromUpdates = sigs.length > 0 ? Math.max(...sigs) : null;
    const maxSignificance =
        maxFromUpdates != null
            ? maxFromUpdates
            : g.maxSignificance != null && Number.isFinite(Number(g.maxSignificance))
              ? Math.round(Number(g.maxSignificance))
              : null;
    const reportClaim = g.reportClaim != null ? String(g.reportClaim) : '';
    const reportCitation =
        g.reportCitation != null && String(g.reportCitation).trim()
            ? String(g.reportCitation).trim()
            : reportClaim;
    return {
        claimKey,
        claimId: g.claimId != null && String(g.claimId).trim() ? String(g.claimId).trim() : null,
        reportClaim,
        reportCitation,
        maxSignificance,
        targetEntityLabels: Array.isArray(g.targetEntityLabels) ? g.targetEntityLabels : [],
        updates,
    };
}

/** Highest significance first; ties broken by update count. */
export function sortClaimGroupsBySignificanceDesc(groups) {
    if (!Array.isArray(groups)) return [];
    return [...groups].sort((a, b) => {
        const sig = (Number(b.maxSignificance) || 0) - (Number(a.maxSignificance) || 0);
        if (sig !== 0) return sig;
        return (b.updates?.length || 0) - (a.updates?.length || 0);
    });
}

/**
 * Fallback when API has flat clusters only (older cache / server).
 * @param {ReturnType<typeof normalizeDeltaClusterForUi>[]} clusters
 */
/**
 * Normalizes a full {@code EntityDeltaClusterResponse} JSON payload for UI (clusters + claimGroups).
 * @param {object|null|undefined} payload
 */
export function normalizeDeltaClusterApiPayload(payload) {
    if (!payload || typeof payload !== 'object') {
        return buildEmptyDeltaClusterList();
    }
    const rawClusters = Array.isArray(payload.clusters) ? payload.clusters : [];
    const clusters = rawClusters.map(normalizeDeltaClusterForUi).filter(Boolean);
    const rawGroups = Array.isArray(payload.claimGroups) ? payload.claimGroups : [];
    let claimGroups = sortClaimGroupsBySignificanceDesc(
        rawGroups.map((g, i) => normalizeClaimGroupForUi(g, i)).filter(Boolean)
    );
    if (claimGroups.length === 0 && clusters.length > 0) {
        claimGroups = claimGroupsFromFlatClusters(clusters);
    }
    return {
        ...payload,
        clusters,
        claimGroups,
    };
}

/**
 * Merges incremental STOMP {@code partialClaimGroups} into prior state by {@code claimKey} (upsert).
 * @param {Array<{ claimKey: string }>} prevGroups
 * @param {Array<{ claimKey: string }>} partialGroups
 */
export function mergePartialClaimGroupsByClaimKey(prevGroups, partialGroups) {
    const byKey = new Map();
    for (const g of prevGroups || []) {
        if (g?.claimKey) {
            byKey.set(g.claimKey, g);
        }
    }
    for (const g of partialGroups || []) {
        if (g?.claimKey) {
            byKey.set(g.claimKey, g);
        }
    }
    return sortClaimGroupsBySignificanceDesc(Array.from(byKey.values()));
}

/**
 * Normalizes raw {@code ClaimGroupInfo} objects from STOMP JSON.
 * @param {object[]} rawGroups
 */
export function normalizePartialClaimGroupsRaw(rawGroups) {
    if (!Array.isArray(rawGroups)) {
        return [];
    }
    return rawGroups.map((g, i) => normalizeClaimGroupForUi(g, i)).filter(Boolean);
}

/** STOMP {@code PARTIAL} frames carry flat {@code partialClusters}; normalize to claim groups. */
export function claimGroupsFromPartialClusters(partialClusters) {
    if (!Array.isArray(partialClusters) || partialClusters.length === 0) {
        return [];
    }
    return normalizeDeltaClusterApiPayload({clusters: partialClusters}).claimGroups;
}

export function claimGroupsFromFlatClusters(clusters) {
    if (!Array.isArray(clusters) || clusters.length === 0) return [];
    /** @type {Map<string, typeof clusters>} */
    const byKey = new Map();
    for (const c of clusters) {
        if (!c) continue;
        const cu = Array.isArray(c.claimUpdates) && c.claimUpdates[0] ? c.claimUpdates[0] : null;
        const key =
            (cu?.reportClaimId && String(cu.reportClaimId).trim()) ||
            `text:${(cu?.reportClaim || c.description || '').trim().toLowerCase()}` ||
            `c:${c.clusterId}`;
        if (!byKey.has(key)) byKey.set(key, []);
        byKey.get(key).push(c);
    }
    const out = [];
    let uid = 1;
    let idx = 0;
    for (const rows of byKey.values()) {
        const normRows = rows.map((r) => normalizeDeltaClusterForUi(r)).filter(Boolean);
        if (normRows.length === 0) continue;
        const first = normRows[0];
        const cu0 = first?.claimUpdates?.[0];
        const claimKey =
            (cu0?.reportClaimId && String(cu0.reportClaimId).trim()) || `claim-${idx}`;
        const updates = normRows
            .map((row) => {
                const cu = row.claimUpdates?.[0];
                const rawSeg = Array.isArray(row.segments) ? row.segments : [];
                const segments = rawSeg.map(normalizeSegmentForUi).filter(Boolean);
                if (!segments.length || !cu?.sourceUpdate?.trim()) return null;
                const summary = cu.summary ?? '';
                const sourceUpdate = cu.sourceUpdate ?? '';
                if (isAbsenceOnlyCorpusNarrative(`${summary} ${sourceUpdate}`)) return null;
                return {
                    updateId: String(uid++),
                    name: row.title ?? '',
                    summary,
                    sourceUpdate,
                    relation: cu.relation ?? null,
                    significance: row.significance,
                    segments,
                };
            })
            .filter(Boolean);
        if (updates.length === 0) {
            idx += 1;
            continue;
        }
        const maxSig = Math.max(...normRows.map((r) => (r.significance != null ? Number(r.significance) : 0)), 0);
        const labels = [...new Set(normRows.flatMap((r) => r.targetEntityLabels || []))];
        const reportClaim = cu0?.reportClaim ?? first.description ?? '';
        const reportCitation =
            cu0?.reportCitation != null && String(cu0.reportCitation).trim()
                ? String(cu0.reportCitation).trim()
                : reportClaim;
        out.push({
            claimKey,
            claimId: cu0?.reportClaimId ? String(cu0.reportClaimId).trim() : null,
            reportClaim,
            reportCitation,
            maxSignificance: maxSig || null,
            targetEntityLabels: labels,
            updates,
        });
        idx += 1;
    }
    out.sort(
        (a, b) =>
            (b.maxSignificance ?? 0) - (a.maxSignificance ?? 0) || b.updates.length - a.updates.length
    );
    return out;
}

/**
 * Synthetic "stories" for DeltaStoryModal: one per corpus update.
 * @param {Array<{ updates: Array<{ updateId: string, summary?: string, sourceUpdate?: string, name?: string, segments: object[] }> }>} claimGroups
 */
export function flattenClaimUpdatesToModalStories(claimGroups) {
    if (!Array.isArray(claimGroups)) return [];
    return claimGroups.flatMap((g) =>
        (g.updates || []).map((u) => ({
            clusterId: u.updateId,
            title: u.summary?.trim() || u.sourceUpdate?.trim() || u.name?.trim() || 'Update',
            description: u.sourceUpdate ?? '',
            tags: [],
            segments: Array.isArray(u.segments) ? u.segments : [],
            significance: u.significance,
            targetEntityLabels: Array.isArray(g.targetEntityLabels) ? g.targetEntityLabels : [],
            targetEntityIds: [],
            claimUpdates: [],
            _claimReport: (() => {
                const raw = g.reportClaim != null ? String(g.reportClaim) : '';
                return stripReportSaysPrefix(raw) || raw;
            })(),
        }))
    );
}

/**
 * Report-aware delta clusters (significance-ranked). POST /api/entities/delta-clusters
 *
 * @param {{ publishedAt: string, entities?: Array<{id: string, label: string}>, reportText: string, reportId?: string, deltaMode?: string }} args
 */
export async function getReportDeltaClusters({publishedAt, entities = [], reportText, reportId, deltaMode}) {
    const text = reportText != null ? String(reportText).trim() : '';
    if (!publishedAt || !text) {
        return buildEmptyDeltaClusterList();
    }

    try {
        const response = await fetch(`${API_BASE}/entities/delta-clusters`, {
            method: 'POST',
            headers: headersWithOptionalDeltaRun({
                reportId,
                deltaMode,
                base: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            }),
            body: JSON.stringify({
                publishedAt,
                entities: Array.isArray(entities) ? entities : [],
                reportText: text,
            }),
        });

        const rawBody = await response.text();

        if (!response.ok) {
            console.warn('[deltas] delta-clusters request failed:', response.status, response.statusText);
            return buildEmptyDeltaClusterList();
        }

        const payload = rawBody ? JSON.parse(rawBody) : {};
        return normalizeDeltaClusterApiPayload(payload);
    } catch (error) {
        console.warn('[deltas] delta-clusters request error:', error);
        return buildEmptyDeltaClusterList();
    }
}

/**
 * Starts async report delta clustering. Subscribe to {@code topic} (STOMP) before awaiting if messages must not be missed;
 * the client should pass a pre-generated {@code jobId} and connect to {@code getAwpBackendOrigin()}/ws first.
 *
 * @param {{ publishedAt: string, entities?: Array<{id: string, label: string}>, reportText: string, reportId?: string, deltaMode?: string, jobId: string }} args
 * @returns {Promise<{ jobId: string, topic: string }>}
 */
export async function startReportDeltaClusters({publishedAt, entities = [], reportText, reportId, deltaMode, jobId}) {
    const text = reportText != null ? String(reportText).trim() : '';
    const jid = jobId != null ? String(jobId).trim() : '';
    if (!publishedAt || !text) {
        throw new Error('startReportDeltaClusters: publishedAt and non-empty reportText are required');
    }
    if (!jid) {
        throw new Error('startReportDeltaClusters: jobId is required');
    }

    const response = await fetch(`${API_BASE}/entities/delta-clusters/start`, {
        method: 'POST',
        headers: headersWithOptionalDeltaRun({
            reportId,
            deltaMode,
            base: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        }),
        body: JSON.stringify({
            jobId: jid,
            publishedAt,
            entities: Array.isArray(entities) ? entities : [],
            reportText: text,
        }),
    });

    const rawBody = await response.text();
    if (!response.ok) {
        console.warn('[deltas] delta-clusters/start failed:', response.status, response.statusText, rawBody);
        let detail = rawBody?.trim() || '';
        if (detail) {
            try {
                const parsed = JSON.parse(detail);
                detail = parsed.message || parsed.error || detail;
            } catch {
                /* use raw text */
            }
        }
        const suffix = detail ? `: ${detail}` : '';
        throw new Error(`delta-clusters/start failed (${response.status})${suffix}`);
    }

    return rawBody ? JSON.parse(rawBody) : {};
}

function buildEmptyTimelineStories() {
    return {
        stories: [],
        fromCache: false,
        profileKey: null,
        windowStart: null,
        windowEnd: null,
    };
}

function mapTimelineChunkToStories(chunk) {
    if (!chunk || typeof chunk !== 'object') return [];
    const segmentById = new Map();
    for (const s of chunk.segments || []) {
        if (s != null && s.segmentId != null) {
            segmentById.set(s.segmentId, normalizeSegmentForUi(s));
        }
    }
    const updates = Array.isArray(chunk.updates) ? chunk.updates : [];
    return updates.map((u, i) => {
        const ids = Array.isArray(u.segmentIds) ? u.segmentIds : [];
        const segments = ids.map((id) => segmentById.get(id)).filter(Boolean);
        const descParts = [u.date, u.description].filter(Boolean);
        return {
            clusterId: `timeline-${i}`,
            title: u.title || `Update ${i + 1}`,
            description: descParts.join(' — ') || '',
            tags: Array.isArray(u.tags) ? u.tags : [],
            segments,
            significance: u.important ? 5 : null,
            targetEntityLabels: Array.isArray(u.entities) ? u.entities : [],
            fintelContrastSummary: u.fintelContrastSummary,
        };
    });
}

/**
 * Delta timeline chunk (baseline → today window, UTC dates). POST /api/entities/delta-timeline
 *
 * @param {{ baseline: string, reportText: string, windowStart: string, windowEnd: string, profileKey?: string, reportId?: string, deltaMode?: string }} args
 * @returns {Promise<{ stories: object[], fromCache: boolean, profileKey?: string, windowStart?: string, windowEnd?: string }>}
 */
export async function getDeltaTimelineStories({
    baseline,
    reportText,
    windowStart,
    windowEnd,
    profileKey,
    reportId,
    deltaMode,
}) {
    const text = reportText != null ? String(reportText).trim() : '';
    if (!baseline || !text || !windowStart || !windowEnd) {
        return buildEmptyTimelineStories();
    }

    try {
        const body = {
            baseline,
            reportText: text,
            windowStart: String(windowStart).trim(),
            windowEnd: String(windowEnd).trim(),
        };
        if (profileKey && String(profileKey).trim()) {
            body.profileKey = String(profileKey).trim();
        }

        const response = await fetch(`${API_BASE}/entities/delta-timeline`, {
            method: 'POST',
            headers: headersWithOptionalDeltaRun({
                reportId,
                deltaMode,
                base: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            }),
            body: JSON.stringify(body),
        });

        const rawBody = await response.text();

        if (!response.ok) {
            console.warn('[deltas] delta-timeline request failed:', response.status, response.statusText);
            return buildEmptyTimelineStories();
        }

        const payload = rawBody ? JSON.parse(rawBody) : {};
        return {
            stories: mapTimelineChunkToStories(payload),
            fromCache: Boolean(payload.fromCache),
            profileKey: payload.profileKey ?? null,
            windowStart: payload.windowStart ?? null,
            windowEnd: payload.windowEnd ?? null,
        };
    } catch (error) {
        console.warn('[deltas] delta-timeline request error:', error);
        return buildEmptyTimelineStories();
    }
}

/**
 * Report entity mention summary since report publication.
 *
 * Request shape:
 * {
 *   entities: string[],
 *   publishedAt: string | number
 * }
 *
 * Response shape:
 * {
 *   meta: {totalEntities: number, totalMentions: number},
 *   entities: Array<{id: string, label: string, description: string, mentionsCount: number}>
 * }
 */
export async function getEntityMentionsSummary({entities, publishedAt, reportId, deltaMode}) {
    console.log('[DeltaPanel] Request payload:', {entities, publishedAt});
    if (!publishedAt || !Array.isArray(entities) || entities.length === 0) {
        return buildEmptyMentionsResponse();
    }

    try {
        const response = await fetch(`${API_BASE}/entities/mention-counts`, {
            method: 'POST',
            headers: headersWithOptionalDeltaRun({
                reportId,
                deltaMode,
                base: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            }),
            body: JSON.stringify({
                publishedAt,
                entities,
            }),
        });
        console.log('[deltas] mention-counts response status:', response.status, response.statusText);

        const rawBody = await response.text();
        console.log('[deltas] mention-counts raw response:', {
            status: response.status,
            statusText: response.statusText,
            body: rawBody,
        });

        if (!response.ok) {
            console.warn('[deltas] mention-counts request failed:', response.status, response.statusText);
            return buildEmptyMentionsResponse();
        }

        const payload = rawBody ? JSON.parse(rawBody) : {};
        return payload;
    } catch (error) {
        console.warn('[deltas] mention-counts request error:', error);
        return buildEmptyMentionsResponse();
    }

}

/**
 * Grouped stories (clusters) for one entity delta.
 *
 * Request shape:
 * {
 *   entityId: string,
 *   publishedAt: string | number
 * }
 *
 * Response shape:
 * {
 *   entityId: string,
 *   entityLabel: string,
 *   entityDescription: string,
 *   publishedAfter: string,
 *   clusteredAt: string,
 *   fromCache: boolean,
 *   clusters: Array<{
 *     clusterId: number,
 *     title: string,
 *     description: string,
 *     tags: string[],
 *     segments: Array<{
 *       segmentId: number,
 *       docId: number,
 *       docTitle: string,
 *       publishDate: string,
 *       content: string
 *     }>
 *   }>
 * }
 */
export async function getEntityDeltaStories({entityId, publishedAt, reportId, deltaMode}) {
    if (!entityId || !publishedAt) {
        return buildEmptyClustersResponse(String(entityId || ''));
    }

    try {
        const response = await fetch(`${API_BASE}/entities/clusters`, {
            method: 'POST',
            headers: headersWithOptionalDeltaRun({
                reportId,
                deltaMode,
                base: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            }),
            body: JSON.stringify({
                entityId,
                publishedAt,
            }),
        });

        const rawBody = await response.text();

        if (!response.ok) {
            console.warn('[deltas] clusters request failed:', response.status, response.statusText);
            return buildEmptyClustersResponse(String(entityId || ''));
        }

        const payload = rawBody ? JSON.parse(rawBody) : {};
        return payload;
    } catch (error) {
        console.warn('[deltas] clusters request error:', error);
        return buildEmptyClustersResponse(String(entityId || ''));
    }
}

